/**
 * The frame around every page: the navy sidebar (logo, pages, counts, you), the top bar (where you
 * are, search, quick add, theme) and the content. It is built once after sign-in and kept while you
 * move between pages, so the sidebar, scroll and focus behave like one app.
 *
 * Accessibility: a skip link to the content, landmarks (navigation, banner, main), aria-current on
 * the page you are on, a visible focus ring everywhere, every icon button labelled, and focus moved
 * to the new page's heading when you change page.
 */
import type { Db } from "../data/db.ts";
import { h } from "./dom.ts";
import { icon } from "./icons.ts";
import { PAGES, pageFor, type Page } from "./nav.ts";
import { isDark, onPrefs, prefs, setPrefs, type Theme } from "./prefs.ts";
import { openPalette } from "./palette.ts";
import { showShortcuts } from "./shortcuts.ts";
import { pageCut } from "./katana.ts";
import { toast } from "./toast.ts";
import { langAttrs, t } from "./i18n.ts";

export interface Counts { replies: number; late: number; today: number }

export interface Shell {
  frame: HTMLElement;
  /** Puts a page in the content area. `first` is the first page after sign-in (no effects, no focus move). */
  show(view: HTMLElement, hash: string, first?: boolean): void;
  setCounts(c: Counts): void;
  /** Extra words after the section in the top bar, such as a lead's name. */
  setDetail(text: string): void;
}

export const initials = (nameOrEmail: string): string => {
  const base = nameOrEmail.includes("@") ? nameOrEmail.split("@")[0]! : nameOrEmail;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "R") + (parts.length > 1 ? parts[parts.length - 1]![0]! : (parts[0]?.[1] ?? ""))).toUpperCase();
};

let accountName = "";
/** The full name from the Google account, set at sign-in. */
export const setAccountName = (name: string | undefined): void => { accountName = (name ?? "").trim(); };

/** Your full name: the one set in Settings, then the Google account's, then the first part of the email. */
export function fullName(email: string): string {
  const set = prefs().displayName.trim();
  if (set) return set;
  if (accountName) return accountName;
  const first = email.split("@")[0]!.split(/[._-]/)[0] ?? "";
  return first ? first[0]!.toUpperCase() + first.slice(1) : "";
}

/** The name to greet you by: your first name. */
export const displayName = (email: string): string => fullName(email).split(/\s+/)[0] ?? "";

const tr = (x: string) => t(x);
const THEME_NEXT: Record<Theme, Theme> = { system: "light", light: "dark", dark: "system" };
const THEME_LABEL: Record<Theme, string> = { system: "Theme: match computer", light: "Theme: light", dark: "Theme: dark" };

export function createShell(db: Db, email: string): Shell {
  const links = new Map<string, HTMLAnchorElement>();
  const counters = { leads: h("span", { class: "count", hidden: true }), today: h("span", { class: "count", hidden: true }) };

  const navLink = (p: Page) => {
    const a = h("a", { href: p.href, title: t(p.label) },
      icon(p.icon), h("span", { class: "lbl" }, t(p.label)),
      p.key === "leads" ? counters.leads : p.key === "today" ? counters.today : null,
      h("span", { class: "kbd", "aria-hidden": "true" }, h("kbd", null, "G"), h("kbd", null, p.go.toUpperCase())),
    );
    links.set(p.key, a);
    return a;
  };
  const groups = (["Work", "Clients", "Library"] as const).map((g) =>
    h("div", { class: "side-group" },
      h("div", { class: "side-label", id: `nav-${g}` }, t(g)),
      h("div", { class: "side-nav", role: "list", "aria-labelledby": `nav-${g}` }, PAGES.filter((p) => p.group === g).map((p) => h("div", { role: "listitem" }, navLink(p)))),
    ));

  const app = h("div", { class: "app" });
  const closeDrawer = () => app.classList.remove("drawer-open");
  const collapseBtn = h("button", { type: "button", class: "side-btn collapse-btn", onClick: () => { setPrefs({ sidebarCollapsed: !prefs().sidebarCollapsed }); paintCollapse(); } });
  const paintCollapse = () => {
    const c = prefs().sidebarCollapsed;
    collapseBtn.replaceChildren(icon(c ? "expand" : "collapse"), h("span", { class: "lbl" }, t(c ? "Expand sidebar" : "Collapse sidebar")));
    collapseBtn.setAttribute("aria-label", t(c ? "Expand sidebar" : "Collapse sidebar"));
    collapseBtn.setAttribute("aria-expanded", String(!c));
  };
  paintCollapse();

  const name = fullName(email);
  // You: click your name to sign out (a disclosure menu). Shortcuts have their own button above.
  const userMenu = h("div", { class: "user-menu", id: "user-menu", hidden: true },
    h("button", { type: "button", class: "side-btn", onClick: () => void db.signOut() }, icon("logout"), h("span", { class: "lbl" }, t("Sign out"))),
  );
  const userBtn = h("button", { type: "button", class: "side-user", "aria-expanded": "false", "aria-controls": "user-menu", title: `${name} (${email})` },
    h("span", { class: "avatar", "aria-hidden": "true" }, initials(name || email)),
    h("span", { class: "side-user-text" }, h("b", null, name || email), h("span", null, email)),
    h("span", { class: "chev lbl", "aria-hidden": "true" }, "\u25B4"),
  );
  const closeUser = () => { userMenu.hidden = true; userBtn.setAttribute("aria-expanded", "false"); };
  userBtn.addEventListener("click", () => {
    const open = userMenu.hidden;
    userMenu.hidden = !open;
    userBtn.setAttribute("aria-expanded", String(open));
    if (open) userMenu.querySelector<HTMLElement>("a, button")?.focus();
  });
  userMenu.addEventListener("keydown", (e) => { if (e.key === "Escape") { closeUser(); userBtn.focus(); } });
  document.addEventListener("click", (e) => { if (!userMenu.hidden && !userMenu.contains(e.target as Node) && !userBtn.contains(e.target as Node)) closeUser(); });
  window.addEventListener("hashchange", closeUser);

  const side = h("nav", { class: "side", "aria-label": "Main" },
    h("a", { class: "side-brand", href: "#/", "aria-label": "Ryoma Insight, Today" },
      h("img", { class: "side-logo", src: "/brand/logo-on-dark.svg", alt: "", width: "136", height: "77" }),
      h("img", { class: "side-mark", src: "/brand/mark-on-dark.svg", alt: "", width: "44", height: "44" }),
    ),
    groups,
    h("div", { class: "side-foot" },
      h("button", { type: "button", class: "side-btn", onClick: () => showShortcuts(), "aria-label": t("Keyboard shortcuts") }, icon("keyboard"), h("span", { class: "lbl" }, t("Shortcuts")), h("span", { class: "kbd lbl", "aria-hidden": "true" }, h("kbd", null, "?"))),
      collapseBtn,
      userMenu,
      userBtn,
    ),
  );

  // ---- Top bar
  const crumbSection = h("b", null, "");
  const crumbDetail = h("span", null, "");
  const themeBtn = h("button", { type: "button", class: "icon-btn" });
  const paintTheme = () => {
    const t = prefs().theme;
    themeBtn.replaceChildren(icon(t === "system" ? "monitor" : t === "dark" ? "moon" : "sun"));
    themeBtn.setAttribute("aria-label", tr(THEME_LABEL[t]));
    themeBtn.title = tr(THEME_LABEL[t]);
  };
  themeBtn.addEventListener("click", () => {
    setPrefs({ theme: THEME_NEXT[prefs().theme] });
    paintTheme();
    toast(`${tr(THEME_LABEL[prefs().theme])}${prefs().theme === "system" ? (isDark() ? " (dark)" : " (light)") : ""}`);
  });
  paintTheme();
  onPrefs(() => { paintTheme(); paintCollapse(); });
  const menuBtn = h("button", { type: "button", class: "icon-btn menu-btn", "aria-label": t("Open menu"), onClick: () => app.classList.add("drawer-open") }, icon("menu"));
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const topbar = h("header", { class: "topbar" },
    menuBtn,
    h("a", { class: "top-home", href: "#/", "aria-label": "Today" }, h("img", { class: "top-mark on-light", src: "/brand/mark.svg", alt: "" }), h("img", { class: "top-mark on-dark", src: "/brand/mark-on-dark.svg", alt: "" })),
    h("div", { class: "crumb" }, crumbSection, crumbDetail),
    h("div", { class: "top-actions" },
      db.kind === "memory" ? h("span", { class: "demo-flag", title: "Sample data in this browser only. Nothing is saved." }, t("Demo data")) : "",
      h("button", { type: "button", class: "search-btn", onClick: () => openPalette(db), "aria-label": "Command palette: find a page, lead or audit", "aria-keyshortcuts": isMac ? "Meta+K" : "Control+K" },
        icon("search"), h("span", { class: "lbl" }, t("Search leads, audits, pages")), h("span", { class: "kbd", "aria-hidden": "true" }, h("kbd", null, isMac ? "⌘" : "Ctrl"), h("kbd", null, "K"))),
      h("a", { class: "btn btn-gold", href: "#/leads?add", title: `${t("Add a lead or prospect")} (N)` }, icon("plus"), h("span", { class: "lbl" }, t("New lead"))),
      themeBtn,
    ),
  );

  // Page content stays English and left to right, whatever language the menus are in.
  const main = h("main", { id: "main", tabindex: "-1", lang: "en-GB", dir: "ltr" });
  for (const [k, v] of Object.entries(langAttrs())) app.setAttribute(k, v);
  const toTop = h("button", { type: "button", class: "btn to-top", onClick: () => { window.scrollTo({ top: 0, behavior: "smooth" }); main.focus({ preventScroll: true }); } }, icon("arrow"), t("Back to top"));
  (toTop.querySelector(".ico") as HTMLElement | null)?.style.setProperty("transform", "rotate(-90deg)");
  window.addEventListener("scroll", () => toTop.classList.toggle("show", window.scrollY > 700), { passive: true });
  // Reading guide: a soft band that follows the pointer, for keeping your place in long tables.
  const guide = h("div", { class: "reading-guide", "aria-hidden": "true", hidden: !prefs().readingGuide });
  window.addEventListener("pointermove", (e) => { if (!guide.hidden) guide.style.setProperty("top", `${e.clientY}px`); }, { passive: true });
  onPrefs((p) => { guide.hidden = !p.readingGuide; });
  app.append(
    h("a", { class: "skip-link", href: "#main", onClick: (e: Event) => { e.preventDefault(); main.focus(); } }, t("Skip to content")),
    side,
    h("div", { class: "scrim", onClick: closeDrawer }),
    h("div", { class: "col" }, topbar, main),
    toTop, guide,
  );
  app.addEventListener("keydown", (e) => { if (e.key === "Escape" && app.classList.contains("drawer-open")) { closeDrawer(); menuBtn.focus(); } });

  return {
    frame: app,
    show(view, hash, first = false) {
      const page = pageFor(hash);
      for (const [k, a] of links) {
        if (k === page.key) a.setAttribute("aria-current", "page");
        else a.removeAttribute("aria-current");
      }
      crumbSection.textContent = t(page.label);
      crumbDetail.textContent = "";
      closeDrawer();
      main.replaceChildren(view);
      const heading = main.querySelector("h1");
      document.title = `${heading?.textContent?.trim() || page.label} · Ryoma`;
      if (!first) {
        pageCut(main);
        if (heading) {
          heading.setAttribute("tabindex", "-1");
          heading.focus({ preventScroll: true });
        }
      }
      window.scrollTo(0, 0);
    },
    setCounts(c) {
      const showCounts = prefs().showCounts;
      counters.leads.hidden = !showCounts || !c.replies;
      counters.leads.textContent = String(c.replies);
      counters.leads.className = `count${c.late ? " count-bad" : ""}`;
      counters.leads.title = `${c.replies} waiting for a reply${c.late ? `, ${c.late} late` : ""}`;
      counters.today.hidden = !showCounts || !c.today;
      counters.today.textContent = String(c.today);
      counters.today.title = `${c.today} things due today or late`;
      // The tab shows how many replies are waiting, so it is visible from other tabs.
      const base = document.title.replace(/^\(\d+\) /, "");
      document.title = c.replies && showCounts ? `(${c.replies}) ${base}` : base;
    },
    setDetail(text) {
      crumbDetail.textContent = text ? `/ ${text}` : "";
    },
  };
}
